import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import * as authService from "@/services/auth";
import type { RbacLevel } from "@/config/rbac";
import type { UserProfile } from "@/types/api";

interface AuthState {
  user: UserProfile | null;
  status: "loading" | "authenticated" | "unauthenticated";
  currentTenantId: string | null;
}

interface AuthDerived {
  isSuperadmin: boolean;
  mustChangePassword: boolean;
}

interface AuthApi {
  login: (email: string, password: string) => Promise<UserProfile>;
  verifyMfaLogin: (
    mfaChallengeToken: string,
    code: string,
    useRecoveryCode?: boolean,
  ) => Promise<UserProfile>;
  loginWithPasskey: (email: string) => Promise<UserProfile>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ) => Promise<UserProfile>;
  logout: () => Promise<void>;
  setCurrentTenantId: (tenantId: string | null) => void;
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  hasModuleLevel: (moduleCode: string, level: RbacLevel) => boolean;
}

export type AuthContextValue = AuthState & AuthDerived & AuthApi;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(() =>
    authService.getCurrentUser(),
  );
  const [status, setStatus] = useState<AuthState["status"]>(() =>
    authService.isAuthenticated() ? "authenticated" : "unauthenticated",
  );
  const [currentTenantId, setCurrentTenantIdState] = useState<string | null>(
    () => authService.getCurrentTenantId(),
  );

  useEffect(() => {
    setStatus(
      authService.isAuthenticated() ? "authenticated" : "unauthenticated",
    );
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const u = await authService.login(email, password);
    setUser(u);
    setCurrentTenantIdState(authService.getCurrentTenantId());
    setStatus("authenticated");
    return u;
  }, []);

  const verifyMfaLogin = useCallback(
    async (
      mfaChallengeToken: string,
      code: string,
      useRecoveryCode = false,
    ) => {
      const u = await authService.verifyMfaLogin(
        mfaChallengeToken,
        code,
        useRecoveryCode,
      );
      setUser(u);
      setCurrentTenantIdState(authService.getCurrentTenantId());
      setStatus("authenticated");
      return u;
    },
    [],
  );

  const loginWithPasskey = useCallback(async (email: string) => {
    const u = await authService.loginWithPasskey(email);
    setUser(u);
    setCurrentTenantIdState(authService.getCurrentTenantId());
    setStatus("authenticated");
    return u;
  }, []);

  const changePassword = useCallback(
    async (
      currentPassword: string,
      newPassword: string,
      confirmPassword: string,
    ) => {
      const u = await authService.changePassword(
        currentPassword,
        newPassword,
        confirmPassword,
      );
      setUser(u);
      return u;
    },
    [],
  );

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setCurrentTenantIdState(null);
    setStatus("unauthenticated");
  }, []);

  const setCurrentTenantId = useCallback((tenantId: string | null) => {
    authService.setCurrentTenantId(tenantId);
    setUser(authService.getCurrentUser());
    setCurrentTenantIdState(authService.getCurrentTenantId());
  }, []);

  const isSuperadmin = Boolean(user?.roles.includes("SUPERADMIN"));
  const mustChangePassword = Boolean(user?.mustChangePassword);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      currentTenantId,
      isSuperadmin,
      mustChangePassword,
      login,
      verifyMfaLogin,
      loginWithPasskey,
      changePassword,
      logout,
      setCurrentTenantId,
      hasPermission: authService.hasPermission,
      hasAnyPermission: authService.hasAnyPermission,
      hasModuleLevel: authService.hasModuleLevel,
    }),
    [
      user,
      status,
      currentTenantId,
      isSuperadmin,
      mustChangePassword,
      login,
      verifyMfaLogin,
      loginWithPasskey,
      changePassword,
      logout,
      setCurrentTenantId,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
